'use strict';

export default angular
  .module('maestro.services.data.devices', [
    'maestro.services.utils',
    'maestro.services.webcore'
  ])
  .service('DevicesService', DevicesService)
  .name;

function DevicesService(webcore, $q, CacheService) {

  /**
   * Gets supported device types.
   */
  this.getDeviceTypes = CacheService.cachePromiseFunc(() => {
    return webcore.services.device
      .getSupportedDeviceTypes(false, 'deviceTypes', 'name')
      .then(data => $q.resolve(data), err => $q.reject(err));
  });

  /**
   * Gets supported device types for organization.
   * @param {number} orgId Organization ID
   * @returns {Promise}
   */
  this.getOrganizationDeviceTypes = CacheService.cachePromiseFunc((orgId) => {
    return $q.when(
      webcore.services.device.getSupportedDeviceTypes(false, 'deviceTypes', 'name', orgId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Gets devices on location
   * @param {numeber} locationId Location ID
   * @param {boolean} checkPersistent Check if device is online.
   * @param {boolean} getTags Return device tags.
   * @param {boolean} getProspects Return device prospects (status = 2).
   * @returns {Promise}
   */
  this.getLocationDevices = (locationId, checkPersistent, getTags, getProspects) => {
    if (!locationId || locationId < 0 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }

    return $q.when(
      webcore.services.device.getLocationDevices(locationId, {
        checkPersistent: !!checkPersistent,
        getTags: !!getTags,
        sortBy: 'proxyId',
        sortOrder: 'desc',
        prospect: !!getProspects,
      })
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Register new device.
   * @param {{properties?: {name: string, index?: string, value: string}}} properties Device properties.
   * @param [params] Optional parameters.
   * @param {string} [params.deviceId] Device ID.
   * @param {number} [params.locationId] Specific Location to add this device to.
   * @param {number} [params.deviceType] Type of device being registered.
   * @param {boolean} [params.authToken] If requested, your device will be responsible for securely storing the generated authentication token and including it in all future HTTP requests to Ensemble.
   *   * true - Request an authentication token
   *   * false - Do not use an authentication token
   * @param {string} [params.startDate] The absolute date timestamp when the device is registered.
   * @param {string} [params.desc] Device description.
   * @param {number} [params.goalId] Device goal ID.
   * @param {number} [params.proxyId] Related gateway ID.
   * @returns {Promise}
   */
  this.registerDevice = (properties, params) => {
    return $q.when(
      webcore.services.device.registerDevice(properties, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Simulated Device Copy.
   * Create a device simulator - an exact copy of a real device that would receive all the same data as a real device.
   * Using this API an administrator can set a device as a source simulator and register another simulated device on a required location.
   *
   * @param {number} [locationId] Location ID that contains the source device.
   * @param {string} [deviceId] Device ID acting as a source.
   * @param [params] Request parameters.
   * @param {number} [params.simulatedLocationId] Location ID where a simulated devise will be registered. Optional.
   * @returns {Promise}
   */
  this.simulatedDeviceCopy = (locationId, deviceId, params) => {
    return $q.when(
      webcore.services.device.simulatedDeviceCopy(locationId, deviceId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Deletes devices at specified location.
   * @param {number} locationId
   * @param {string} deviceId
   * @returns {Promise}
   */
  this.deleteDevice = (locationId, deviceId) => {
    if (!locationId || locationId < 0 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!deviceId) {
      return $q.reject('Device ID is not specified!');
    }

    return $q.when(
      webcore.services.device.deleteDevice(locationId, deviceId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets specified device information.
   * @param {string} deviceId
   * @param {number} locationId
   * @param {boolean} [checkConnected]
   * @param {string} [userId]
   * @returns {Promise}
   */
  this.getDevice = (deviceId, locationId, checkConnected, userId) => {
    if (!locationId || locationId < 0 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!deviceId) {
      return $q.reject('Device ID is not specified!');
    }

    return $q.when(
      webcore.services.device.getDevice(deviceId, locationId, checkConnected, userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Allows to get devices by supplying params object.
   * We are not directly sending it as we better filter out params and use only those that are valid.
   * For admins only.
   * Default rowCount = 300 - magic number. Check CC-433 task.
   *
   * @param {number} [organizationId] optional organization ID
   * @param [params] Request parameters.
   * @param {number} [params.groupId] Group ID to search within.
   * @param {number} [params.userId] Filter by user ID.
   * @param {number} [params.locationId] Filter by location ID.
   * @param {number} [params.deviceId] Filter by device ID.
   * @param {boolean} [params.tree] Retrieve devices from sub-locations as well.
   * @param {number|number[]} [params.deviceType] Filter by device types. Multiple values supported.
   * @param {string} [params.searchBy] Search by device ID or description. Use * for a wildcard.
   * @param {string} [params.searchTag] Search by tag.
   * @param {string} [params.lessUpdateDate] Request devices, where the last update date is less than this.
   * @param {string} [params.moreUpdateDate] Request devices, where the last update date is more than this.
   * @param {string} [params.paramName] Request devices, which sent this parameter to the cloud.
   * @param {string} [params.paramValue] Requested parameter value.
   * @param {number} [params.limit] Limit the response size by this number.
   * @param {boolean} [params.getTags] Return device tags.
   * @param {string} [params.sortCollection] Collection name to sort (should be "devices" here).
   * @returns {Promise}
   */
  this.getDevicesByParams = (organizationId, params) => {
    let cleanParams = {
      groupId: params.groupId || undefined,
      userId: params.userId || undefined,
      locationId: params.locationId || undefined,
      deviceId: params.deviceId || undefined,
      tree: params.tree == null ? undefined : params.tree,
      deviceType: params.deviceType || undefined,
      searchBy: params.searchBy || undefined,
      searchTag: params.searchTag || undefined,
      lessUpdateDate: params.lessUpdateDate || undefined,
      moreUpdateDate: params.moreUpdateDate || undefined,
      paramName: params.paramName || undefined,
      paramValue: params.paramValue || undefined,
      limit: params.limit || undefined,
      getTags: params.getTags == null ? true : params.getTags,
      sortCollection: params.sortCollection || 'devices',
      sortBy: params.sortBy || 'lastDataReceivedDateMs',
      sortOrder: params.sortOrder || 'desc',
    };

    return $q.when(
      webcore.services.device.searchDevices(organizationId, cleanParams)
        .then(data => {
          // Just make the tags array to be defined even if not exist
          if (cleanParams.getTags && data.devices) {
            for (let device of data.devices) {
              device.tags = device.tags || [];
            }
          }
          return $q.resolve(data);
        }, err => $q.reject(err))
    );
  };

  /**
   * Returns device goals list.
   * @param {number} deviceTypeId
   * @param {string} appName
   * @returns {*}
   */
  this.getDeviceGoals = (deviceTypeId, appName) => {
    return $q.when(
      webcore.services.device.getDeviceGoals(deviceTypeId, appName)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets device's current values (measurements).
   * @param {string} deviceId
   * @param {number} locationId
   * @param {number} userId
   * @param {string} paramName
   * @returns {Promise}
   */
  this.getDeviceCurrentMeasurements = (deviceId, locationId, userId, paramName) => {
    return $q.when(
      webcore.services.device.getCurrentMeasurements(deviceId, locationId, userId, paramName)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Subscribe for device parameters (current measurements) changes
   * @param {number} locationId Location ID.
   * @param {string} [deviceId] Device ID
   * @returns {WsSubscription}
   */
  this.subscribeForDeviceParameters = (locationId, deviceId) => {
    return webcore.services.device.subscribeForDeviceParameters(locationId, deviceId);
  };

  /**
   * Subscribe for device SIM cards changes
   * @param {number} locationId Location ID.
   * @param {string} deviceId Device ID
   * @returns {WsSubscription}
   */
  this.subscribeForSimCards = (locationId, deviceId) => {
    return webcore.services.device.subscribeForSimCards(locationId, deviceId);
  };

  /**
   * Gets the last N measurements for the device (or all of them).
   * See {@link http://docs.iotapps.apiary.io/#reference/device-measurements/get-the-last-n-measurements/get-the-last-n-measurements}
   *
   * The first timestamp reading will contain measured params and their values. All other timestamp readings will only contain the params that changed in value.
   *
   * @param {string} deviceId Device ID to obtain information from.
   * @param {number} rowCount Maximum number of measurements to obtain.
   * @param params Request parameters.
   * @param {number} params.locationId Request information on a specific location.
   * @param {string} [params.startDate] Start date to begin receiving measurements, example: 2014-08-01T12:00:00-08:00.
   * @param {string} [params.endDate] End date to stop receiving measurements, example: 2014-08-01T13:00:00-08:00. Default is the current date.
   * @param {number} [params.userId] User ID to receive measurements from, only called by administrator accounts.
   * @param {string|string[]} [params.paramName] Only obtain measurements for given parameter names. Multiple values can be passed.
   * @param {string} [params.index] Only obtain measurements for parameters with this index number.
   * @param {boolean} [params.reduceNoise] Return tiny parameter values less than defined threshold as zero.
   * @returns {Promise}
   */
  this.getLastNMeasurements = (deviceId, rowCount, params) => {
    return $q.when(
      webcore.services.device.getLastNMeasurements(deviceId, rowCount, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Moves device from one location to another.
   * @param {string} deviceId ID of the device that is moved.
   * @param {number} oldLocationId Id of the location from which device is moved.
   * @param {number} newLocationId Id of the location to which device is moved.
   * @param {string} [deviceMoveTime] Declares when the device was moved. By default, this is set to the current time.
   * @returns {Promise}
   */
  this.moveDeviceToLocation = (deviceId, oldLocationId, newLocationId, deviceMoveTime) => {
    return $q.when(
      webcore.services.device.moveDeviceToLocation(deviceId, oldLocationId, newLocationId, deviceMoveTime)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Update specified device info.
   * @param {string} deviceId
   * @param {number} locationId
   * @param {Object} model
   * @returns {Promise}
   */
  this.updateDeviceAtLocation = (deviceId, locationId, model) => {
    return $q.when(
      webcore.services.device.updateDeviceAtLocation(deviceId, locationId, model)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Sends command to the specified device.
   * @param {string} deviceId
   * @param {number} locationId
   * @param {Object} command object
   * @returns {Promise}
   */
  this.sendCommandToDevice = (deviceId, locationId, command) => {
    return $q.when(
      webcore.services.device.sendCommandToDevice(deviceId, locationId, command)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Activate SIM card.
   * @param {number} locationId
   * @param {string} deviceId
   * @param {number} simId
   * @param {boolean} activate
   */
  this.activateSimCard = (locationId, deviceId, simId, activate) => {
    let params = {
      simId: simId,
      status: activate ? 1 : 5,
    };
    return $q.when(
      webcore.services.device.activateSimCard(locationId, deviceId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // region Device Properties ----------------------------------------

  /**
   * This method allows to get device properties or a specific property.
   * Device Property keys and values can be 250 characters maximum.
   *
   * @param {string} deviceId Device ID to obtain information on.
   * @param params Request parameters
   * @param {number} params.locationId Device location ID.
   * @param {string} [params.name] Optional name of the property to retrieve.
   * @param {number} [params.index] Optional index number of the property to retrieve.
   * @returns {Promise}
   */
  this.getDeviceProperties = (deviceId, params) => {
    return $q.when(
      webcore.services.device.getDeviceProperties(deviceId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * This method allows to delete device property.
   * @param {string} deviceId Device ID to obtain information on.
   * @param params Request parameters
   * @param {number} params.locationId Device location ID.
   * @param {string} params.name Name of the property to delete.
   * @param {string} [params.index] Optional index number of the property to delete.
   * @param {number} [params.userId] Device owner ID for access by an administrator.
   * @returns {Promise}
   */
  this.deleteDeviceProperty = (deviceId, params) => {
    return $q.when(
      webcore.services.device.deleteDeviceProperty(deviceId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * This method allows to set device properties or a specific property.
   * @param {string} deviceId Device ID to obtain information on.
   * @param {number} locationId Device location ID.
   * @param {{property?: Array<{name: string, index?: string, content: string}>}} model Device properties.
   * @returns {Promise}
   */
  this.setDeviceProperties = (deviceId, locationId, model) => {
    return $q.when(
      webcore.services.device.setDeviceProperties(deviceId, locationId, model)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // endregion

  // region Device Models ----------------------------------------

  /**
   * Gets the device categories and models for specific brand.
   * @param [params] Request parameters.
   * @param {string} [params.modelId] Particular device model to return.
   * @param {string} [params.brand] Get text data for specific brand, otherwise data for default brand returned.
   * @param {string} [params.lang] Get text data for specific language, otherwise - data for all languages.
   * @param {boolean} [params.hidden] Request hidden categories and brand, which are not returned by default.
   * @param {string} [params.searchBy] Search criterion. Use * for a wildcard.
   * @param {number} [params.includePairingType] Filter models by pairing type bitmask.
   * @param {number} [params.excludePairingType] Exclude models by pairing type bitmask.
   * @returns {Promise}
   */
  this.getDeviceModels = CacheService.cachePromiseFunc((params) => {
    return $q.when(
      webcore.services.device.getDeviceModels(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  // endregion

  // region Device Configuration ----------------------------------------

  /**
   * Get device parameters.
   * @param {string} [paramName] Name of the specific parameter to get.
   * @returns {Promise}
   */
  this.getDeviceParameters = CacheService.cachePromiseFunc((paramName) => {
    return $q.when(
      webcore.services.device.getDeviceParameters(paramName)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Link specified device to location space.
   * @param {string} deviceId
   * @param {number} locationId
   * @param {string} spaceId
   * @returns {Promise}
   */
  this.linkSpace = (deviceId, locationId, spaceId) => {
    return $q.when(
      webcore.services.device.linkSpace(deviceId, locationId, spaceId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Unlink specified device from location space.
   * @param {string} deviceId
   * @param {number} locationId
   * @param {number} spaceId
   * @returns {Promise}
   */
  this.unlinkSpace = (deviceId, locationId, spaceId) => {
    return $q.when(
      webcore.services.device.unlinkSpace(deviceId, locationId, spaceId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // endregion

}
